/**
 * Задания дня (ISSUE-49): три постоянных задания, цели и награды растут с лучшей волной игрока на начало дня.
 * День — тот же, что у серии (`streakDayIndex`). Счётчики ведёт сервер по принятым журналам и покупкам; клиент только
 * показывает `DailyTasksState`.
 */
import type { BalanceData, DailyTaskClaim, DailyTaskId, DailyTasksDef, DailyTasksState } from "@newfungame/contracts";
import { zombieCount } from "../sim/waves";
import { waveBaseReward } from "./income";
import { streakDayIndex, streakDayStartMs } from "./streak";

export const DAILY_TASK_IDS: readonly DailyTaskId[] = ["kills", "waves", "upgrades"];

export type DailyTaskTargets = Record<DailyTaskId, number>;

/** Что сервер хранит о дне игрока. */
export interface DailyTaskCounters {
  /** Лучшая волна на начало дня: цели и награды не двигаются, пока игрок растёт в этот же день. */
  baseWave: number;
  kills: number;
  waves: number;
  upgrades: number;
  claimed: readonly string[];
}

/** Цели дня для лучшей волны `bestWave`. */
export function dailyTaskTargets(b: BalanceData, def: DailyTasksDef, bestWave: number): DailyTaskTargets {
  const kills = def.killsWaves * zombieCount(b, Math.max(1, bestWave));
  return {
    kills: Math.ceil(kills / 10) * 10,
    waves: Math.min(def.waves.max, def.waves.base + Math.floor(bestWave / def.waves.perBestWaves)),
    upgrades: def.upgrades,
  };
}

/** Монеты за одно задание. */
export function dailyTaskCoins(b: BalanceData, def: DailyTasksDef, bestWave: number): number {
  return def.rewardWaves * waveBaseReward(b, bestWave);
}

/** Монеты мешка за все три задания. */
export function dailyTasksBonusCoins(b: BalanceData, def: DailyTasksDef, bestWave: number): number {
  return def.bonusWaves * waveBaseReward(b, bestWave);
}

/** Задания на момент `nowMs`; `null` — в данных баланса заданий нет. */
export function dailyTasksState(b: BalanceData, c: DailyTaskCounters, nowMs: number): DailyTasksState | null {
  const def = b.dailyTasks;
  if (!def) return null;
  const targets = dailyTaskTargets(b, def, c.baseWave);
  const coins = dailyTaskCoins(b, def, c.baseWave);
  return {
    tasks: DAILY_TASK_IDS.map((id) => ({
      id,
      target: targets[id],
      progress: Math.min(c[id], targets[id]),
      coins,
      claimed: c.claimed.includes(id),
    })),
    bonus: { coins: dailyTasksBonusCoins(b, def, c.baseWave), claimed: c.claimed.includes("bonus") },
    resetsAt: streakDayStartMs(b, streakDayIndex(b, nowMs) + 1),
  };
}

/** Все три задания выполнены (забраны или нет). */
export function dailyTasksComplete(s: DailyTasksState): boolean {
  return s.tasks.every((t) => t.progress >= t.target);
}

/** Что можно забрать прямо сейчас: выполненные задания и мешок. */
export function dailyTasksClaimable(s: DailyTasksState): DailyTaskClaim[] {
  const out: DailyTaskClaim[] = s.tasks.filter((t) => t.progress >= t.target && !t.claimed).map((t) => t.id);
  if (dailyTasksComplete(s) && !s.bonus.claimed) out.push("bonus");
  return out;
}
